import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  NumberInput,
  NumberInputField,
  HStack,
  Text,
  Icon,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import {
  FiChevronDown,
  FiChevronUp,
  FiMessageCircle,
  FiSend,
} from "react-icons/fi";

const API_BASE =
  process.env.REACT_APP_API_BASE || "https://api.globalwineinstitute.org";
const APP_X_API_KEY =
  process.env.REACT_APP_API_KEY || "f3bb4afa55070cc052d1b888d4f7465a";

const SUGGESTION_CATEGORIES = [
  "UI/UX",
  "Performance",
  "Features",
  "Bug",
  "Content",
  "Other",
];

const __DEBUG_FEEDBACK__ = Boolean(
  (typeof window !== "undefined" && window?.GWBI_DEBUG) ||
    process.env.REACT_APP_DEBUG_FEEDBACK
);

const maskKey = (k) => {
  if (!k || typeof k !== "string") return "<none>";
  if (k.length <= 8) return `${k.slice(0, 2)}…${k.slice(-2)}`;
  return `${k.slice(0, 4)}…${k.slice(-4)}`;
};

const clampImportance = (value) => {
  if (!Number.isFinite(value)) return 3;
  return Math.min(5, Math.max(1, Math.round(value)));
};

const getCurrentUserId = () => {
  try {
    const cached =
      localStorage.getItem("userprofile") ||
      sessionStorage.getItem("userprofile");
    if (cached) {
      const parsed = JSON.parse(cached);
      const userId =
        parsed?.user?.id ||
        parsed?.profile?.user?.id ||
        parsed?.profile?.id ||
        parsed?.id ||
        null;
      if (userId) return userId;
    }
  } catch (err) {}
  const stored = Number(localStorage.getItem("userId"));
  if (Number.isFinite(stored)) return stored;
  return null;
};

const getCurrentUserEmail = () => {
  try {
    const cached =
      localStorage.getItem("userprofile") ||
      sessionStorage.getItem("userprofile");
    if (cached) {
      const parsed = JSON.parse(cached);
      const email =
        parsed?.user?.email ||
        parsed?.profile?.user?.email ||
        parsed?.profile?.email ||
        parsed?.email ||
        null;
      if (email) return email;
    }
  } catch (err) {}
  const stored =
    localStorage.getItem("userEmail") || sessionStorage.getItem("userEmail");
  if (stored && typeof stored === "string") return stored;
  return null;
};

export const submitSuggestion = async ({
  title,
  description,
  category = "Other",
  name = "",
  email = "",
  importance = 3,
}) => {
  const payload = {
    title: (title || "").trim(),
    description: (description || "").trim(),
    category: SUGGESTION_CATEGORIES.includes(category) ? category : "Other",
    name: (name || "").trim() || null,
    email: (email || "").trim() || null,
    importance: clampImportance(Number(importance)),
  };

  const maybeUserId = getCurrentUserId();
  const maybeEmail = getCurrentUserEmail();
  if (maybeUserId) payload.user_id = maybeUserId;
  if (!payload.email && maybeEmail) payload.email = maybeEmail;

  if (!payload.title) throw new Error("A title is required.");
  if (!payload.description) throw new Error("Please describe your idea.");

  if (__DEBUG_FEEDBACK__) {
    console.info("[feedback] POST /api/suggestions", {
      apiBase: API_BASE,
      key: maskKey(APP_X_API_KEY),
      hasTitle: Boolean(payload.title),
      descLen: payload.description?.length || 0,
      category: payload.category,
      importance: payload.importance,
      user_id: payload.user_id ?? null,
    });
  }

  const endpoint = `${API_BASE}/api/suggestions`;
  let res, text, data;
  try {
    res = await fetch(endpoint, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        "X-API-KEY": APP_X_API_KEY,
        ...(maybeUserId ? { "X-User-Id": String(maybeUserId) } : {}),
        ...(maybeEmail ? { "X-User-Email": String(maybeEmail) } : {}),
      },
      credentials: "include",
      body: JSON.stringify(payload),
    });
  } catch (netErr) {
    if (__DEBUG_FEEDBACK__) {
      console.error("[feedback] network error", netErr);
    }
    throw new Error("Network error while contacting the feedback API.");
  }

  try {
    text = await res.text();
    data = text ? JSON.parse(text) : {};
  } catch (_) {
    data = {};
  }

  if (!res.ok) {
    if (__DEBUG_FEEDBACK__) {
      console.error("[feedback] API failure", {
        status: res.status,
        statusText: res.statusText,
        body: data || text,
      });
    }
    const msg =
      (data && (data.error || data.message)) ||
      (res.status === 401 || res.status === 403
        ? "Unauthorized access (check X-API-KEY)."
        : `Failed to submit suggestion (HTTP ${res.status}).`);
    throw new Error(msg);
  }

  if (__DEBUG_FEEDBACK__) {
    console.info("[feedback] saved", data?.suggestion?.id || data);
  }

  return data.suggestion;
};

const defaultFormState = Object.freeze({
  title: "",
  description: "",
  name: "",
  email: "",
  category: "Other",
  importance: 3,
});

const COLLAPSED_HEIGHT = 52;
const MIN_OPEN_HEIGHT = 280;
const DEFAULT_OPEN_HEIGHT = 360;
const DRAG_MOVE_THRESHOLD = 6;
const DRAG_ACTIVATION_THRESHOLD = 18;

export const FeedbackWidget = ({
  defaultOpen = false,
  forceOpen = false,
  showLauncher = true,
  mode = "floating",
  cardBackground,
  cardBorderColor,
  cardTextColor,
  labelOverride,
  launchButtonProps = {},
} = {}) => {
  const isInline = mode === "card";
  const isFloating = !isInline;
  const toast = useToast();

  const initialOpen = forceOpen ? true : defaultOpen;
  const [isOpen, setIsOpen] = useState(initialOpen);
  const [formState, setFormState] = useState(defaultFormState);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [widgetHeight, setWidgetHeight] = useState(DEFAULT_OPEN_HEIGHT);
  const [maxHeight, setMaxHeight] = useState(() => {
    if (typeof window === "undefined") {
      return DEFAULT_OPEN_HEIGHT + 120;
    }
    return Math.max(MIN_OPEN_HEIGHT, Math.min(640, window.innerHeight - 96));
  });
  const [dragActive, setDragActive] = useState(false);
  const dragStateRef = useRef(null);

  // Prefill name from userprofile in localStorage/sessionStorage
  useEffect(() => {
    try {
      const cached =
        localStorage.getItem("userprofile") ||
        sessionStorage.getItem("userprofile");
      if (cached) {
        const parsed = JSON.parse(cached);
        const first =
          parsed?.profile?.first_name || parsed?.user?.first_name || "";
        const last =
          parsed?.profile?.last_name || parsed?.user?.last_name || "";
        const fullName = `${first} ${last}`.trim();
        if (fullName) {
          setFormState((prev) => ({
            ...prev,
            name: fullName,
          }));
        }
      }
    } catch (err) {
      console.warn("Failed to prefill name:", err);
    }
  }, []);

  const defaultBgColor = useColorModeValue("white", "gray.800");
  const defaultBannerBg = useColorModeValue("gwbi.700", "gwbi.500");
  const defaultBannerHoverBg = useColorModeValue("gwbi.800", "gwbi.400");
  const defaultBadgeBg = useColorModeValue("whiteAlpha.300", "blackAlpha.300");
  const defaultTaglineColor = useColorModeValue(
    "whiteAlpha.800",
    "whiteAlpha.800"
  );
  const defaultLabelColor = useColorModeValue("black", "gray.200");
  const defaultInputTextColor = useColorModeValue("black", "gray.100");

  const inlineBgColor = cardBackground || "surface.card";
  const inlineTextColor = cardTextColor || "gray.800";
  const inlineLabelColor = "text.muted";
  const inlineInputBg = "white";
  const inlineInputTextColor = "gray.800";
  const inlineInputBorderColor = "gray.300";

  const bgColor = isInline ? inlineBgColor : defaultBgColor;
  const bannerBg = isInline ? inlineBgColor : defaultBannerBg;
  const bannerHoverBg = isInline ? inlineBgColor : defaultBannerHoverBg;
  const badgeBg = isInline ? "surface.muted" : defaultBadgeBg;
  const taglineColor = isInline ? "text.muted" : defaultTaglineColor;
  const headerTextColor = isInline ? inlineTextColor : "white";
  const labelColor = isInline ? inlineLabelColor : defaultLabelColor;
  const inputTextColor = isInline
    ? inlineInputTextColor
    : defaultInputTextColor;
  const inputBg = isInline ? inlineInputBg : undefined;
  const inputBorderColor = isInline ? inlineInputBorderColor : undefined;
  const headerLabel = labelOverride || "Feedback & Suggestions";

  const importanceValue = useMemo(
    () => clampImportance(Number(formState.importance) || 3),
    [formState.importance]
  );

  useEffect(() => {
    if (!isFloating) {
      return undefined;
    }
    if (typeof window === "undefined") {
      return undefined;
    }
    const handleResize = () => {
      setMaxHeight(
        Math.max(MIN_OPEN_HEIGHT, Math.min(640, window.innerHeight - 96))
      );
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isFloating]);

  useEffect(() => {
    if (!isFloating) return;
    setWidgetHeight((height) =>
      Math.min(Math.max(height, MIN_OPEN_HEIGHT), maxHeight)
    );
  }, [maxHeight, isFloating]);

  useEffect(() => {
    if (forceOpen || defaultOpen) {
      setIsOpen(true);
      setWidgetHeight((height) => Math.max(height, DEFAULT_OPEN_HEIGHT));
    }
  }, [forceOpen, defaultOpen]);

  const openPanel = () => {
    if (forceOpen) return;
    setFeedbackMessage("");
    setErrorMessage("");
    setIsOpen(true);
    setWidgetHeight((height) => Math.max(height, DEFAULT_OPEN_HEIGHT));
  };

  const closePanel = () => {
    if (forceOpen) return;
    setIsOpen(false);
    setWidgetHeight(DEFAULT_OPEN_HEIGHT);
    setFeedbackMessage("");
    setErrorMessage("");
  };

  const toggleWidget = () => {
    if (forceOpen) return;
    setFeedbackMessage("");
    setErrorMessage("");
    setIsOpen((prev) => {
      const next = !prev;
      setWidgetHeight((height) =>
        next ? Math.max(height, DEFAULT_OPEN_HEIGHT) : DEFAULT_OPEN_HEIGHT
      );
      return next;
    });
  };

  const beginHeaderDrag = (event) => {
    if (!isFloating || forceOpen) return;
    if (event.pointerType === "mouse" && event.button !== 0) {
      return;
    }
    event.preventDefault();
    const baseHeight = isOpen ? widgetHeight : DEFAULT_OPEN_HEIGHT;
    dragStateRef.current = {
      pointerId: event.pointerId,
      startY: event.clientY,
      startHeight: baseHeight,
      wasOpen: isOpen,
      isDragging: false,
      latestHeight: baseHeight,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
    setDragActive(true);
  };

  const handleHeaderPointerMove = (event) => {
    if (!isFloating) return;
    const drag = dragStateRef.current;
    if (!drag || drag.pointerId !== event.pointerId) {
      return;
    }
    const delta = drag.startY - event.clientY;
    if (!drag.isDragging && Math.abs(delta) > DRAG_MOVE_THRESHOLD) {
      drag.isDragging = true;
    }
    if (!drag.wasOpen && delta > DRAG_ACTIVATION_THRESHOLD) {
      openPanel();
      drag.wasOpen = true;
      drag.isDragging = true;
      drag.startHeight = DEFAULT_OPEN_HEIGHT;
      drag.latestHeight = DEFAULT_OPEN_HEIGHT;
      setWidgetHeight(DEFAULT_OPEN_HEIGHT);
    }
    if (!drag.wasOpen) {
      return;
    }
    const nextHeight = Math.min(
      maxHeight,
      Math.max(MIN_OPEN_HEIGHT, drag.startHeight + delta)
    );
    drag.latestHeight = nextHeight;
    setWidgetHeight(nextHeight);
  };

  const handleHeaderPointerUp = (event) => {
    if (!isFloating) return;
    const drag = dragStateRef.current;
    if (!drag || drag.pointerId !== event.pointerId) {
      return;
    }
    event.currentTarget.releasePointerCapture(event.pointerId);
    dragStateRef.current = null;
    setDragActive(false);
    if (forceOpen) return;
    if (drag.isDragging) {
      if (!drag.wasOpen) {
        return;
      }
      const finalHeight = Math.min(
        maxHeight,
        Math.max(
          MIN_OPEN_HEIGHT,
          drag.latestHeight !== undefined ? drag.latestHeight : drag.startHeight
        )
      );
      if (finalHeight < MIN_OPEN_HEIGHT + 40) {
        closePanel();
      } else {
        setIsOpen(true);
        setWidgetHeight(finalHeight);
      }
      return;
    }
    toggleWidget();
  };

  const handleHeaderPointerCancel = (event) => {
    if (!isFloating) return;
    const drag = dragStateRef.current;
    if (!drag || drag.pointerId !== event.pointerId) {
      return;
    }
    dragStateRef.current = null;
    setDragActive(false);
  };

  const handleHeaderKeyDown = (event) => {
    if (forceOpen) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      if (isOpen) {
        closePanel();
      } else {
        openPanel();
      }
    }
  };

  const updateField = (field, value) => {
    setFormState((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);
    setFeedbackMessage("");
    setErrorMessage("");
    try {
      await submitSuggestion({ ...formState, importance: importanceValue });
      if (isInline) {
        toast({
          title: "Feedback sent",
          description: "Thank you for sharing your idea!",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
        closePanel();
      } else {
        setFeedbackMessage("Thank you for the improvement!");
      }
      setFormState(defaultFormState);
    } catch (err) {
      setErrorMessage(err.message || "Something went wrong. Please retry.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const shouldRender = isFloating ? forceOpen || showLauncher || isOpen : true;
  const containerHeight = isFloating
    ? isOpen
      ? Math.min(widgetHeight, maxHeight)
      : COLLAPSED_HEIGHT
    : null;
  const arrowIcon = isOpen ? FiChevronDown : FiChevronUp;

  const containerBaseProps = isFloating
    ? {
        position: "fixed",
        bottom: { base: 3, md: 4 },
        left: { base: 3, md: 6 },
        width: { base: "88vw", sm: "320px", md: "360px" },
        height: `${containerHeight}px`,
        maxHeight: `${maxHeight}px`,
      }
    : {
        position: "relative",
        width: "100%",
        // Inline card: let the header padding define height so the title
        // isn't clipped against the bottom border when collapsed.
        height: "auto",
      };

  const headerInteractionProps = forceOpen
    ? {}
    : isFloating
    ? {
        onPointerDown: beginHeaderDrag,
        onPointerMove: handleHeaderPointerMove,
        onPointerUp: handleHeaderPointerUp,
        onPointerCancel: handleHeaderPointerCancel,
      }
    : {
        onClick: toggleWidget,
      };

  if (!shouldRender) {
    return null;
  }

  return (
    <Box
      {...containerBaseProps}
      bg={isOpen ? bgColor : bannerBg}
      borderRadius={isFloating ? "xl" : "md"}
      boxShadow={isFloating ? "2xl" : "md"}
      borderWidth="1px"
      borderColor="gwbi.900"
      overflow="hidden"
      zIndex={isFloating ? 1400 : undefined}
      transition={
        isFloating ? (dragActive ? "none" : "height 0.2s ease") : undefined
      }
      display="flex"
      flexDirection="column"
    >
      {!isOpen && !forceOpen && isFloating ? (
        <Button
          size="sm"
          colorScheme="gwbi"
          variant="solid"
          fontSize="xs"
          borderRadius="md"
          position="absolute"
          top={2}
          right={2}
          zIndex={1}
          onPointerDown={(event) => event.stopPropagation()}
          onClick={(event) => {
            event.stopPropagation();
            openPanel();
          }}
          {...launchButtonProps}
        >
          Feedback
        </Button>
      ) : null}
      <Box
        px={isInline ? 5 : 4}
        py={isOpen ? 3 : isInline ? 4 : 2}
        minH={isInline && !isOpen ? "64px" : undefined}
        bg={bannerBg}
        color={headerTextColor}
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        position="relative"
        cursor={forceOpen ? "default" : "pointer"}
        role={forceOpen ? undefined : "button"}
        tabIndex={forceOpen ? -1 : 0}
        aria-label={isOpen ? "Collapse feedback form" : "Open feedback form"}
        aria-expanded={isOpen}
        {...headerInteractionProps}
        onKeyDown={handleHeaderKeyDown}
        transition="background-color 0.2s ease"
        userSelect="none"
        sx={{ touchAction: isFloating ? "none" : undefined }}
        _hover={
          forceOpen
            ? undefined
            : {
                bg: bannerHoverBg,
              }
        }
      >
        <HStack spacing={3} align="center" flex="1" minW={0} pr={2}>
          <Box
            w={8}
            h={8}
            flexShrink={0}
            borderRadius="full"
            bg={badgeBg}
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <FiMessageCircle size={18} />
          </Box>
          <Box minW={0}>
            <Text fontWeight="semibold" fontSize="sm" lineHeight="1.3">
              {headerLabel}
            </Text>
            <Text
              fontSize="xs"
              color={taglineColor}
              display={isOpen ? "block" : "none"}
            >
              Share ideas to improve the institute
            </Text>
          </Box>
        </HStack>
        {!forceOpen ? (
          <HStack spacing={2} align="center">
            {isOpen ? (
              <Button
                size="xs"
                variant="ghost"
                color={headerTextColor}
                _hover={{ bg: "surface.muted" }}
                _active={{ bg: "surface.subtle" }}
                onPointerDown={(event) => event.stopPropagation()}
                onClick={(event) => {
                  event.stopPropagation();
                  closePanel();
                }}
              >
                Close
              </Button>
            ) : null}
            <Box
              w={7}
              h={7}
              borderRadius="full"
              bg={badgeBg}
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Icon as={arrowIcon} boxSize={4} color={headerTextColor} />
            </Box>
          </HStack>
        ) : null}
      </Box>
      {isOpen ? (
        <Box
          flex="1"
          overflowY="auto"
          p={4}
          bg={bgColor}
          color={isInline ? inlineTextColor : undefined}
        >
          <Box
            as="form"
            onSubmit={handleSubmit}
            display="flex"
            flexDirection="column"
            gap={3}
          >
            <FormControl isRequired>
              <FormLabel fontSize="sm" color={labelColor}>
                Title
              </FormLabel>
              <Input
                placeholder="Short summary"
                value={formState.title}
                bg={inputBg}
                borderColor={inputBorderColor}
                color={inputTextColor}
                onChange={(e) => updateField("title", e.target.value)}
              />
            </FormControl>
            <FormControl isRequired>
              <FormLabel fontSize="sm" color={labelColor}>
                Description
              </FormLabel>
              <Textarea
                placeholder="Describe your idea or issue"
                value={formState.description}
                minH="120px"
                bg={inputBg}
                borderColor={inputBorderColor}
                color={inputTextColor}
                onChange={(e) => updateField("description", e.target.value)}
              />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm" color={labelColor}>
                Category
              </FormLabel>
              <Select
                value={formState.category}
                bg={inputBg}
                borderColor={inputBorderColor}
                color={inputTextColor}
                onChange={(e) => updateField("category", e.target.value)}
              >
                {SUGGESTION_CATEGORIES.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </Select>
            </FormControl>
            <HStack spacing={3}>
              <FormControl>
                <FormLabel fontSize="sm" color={labelColor}>
                  Name (optional)
                </FormLabel>
                <Input
                  placeholder="Your name"
                  value={formState.name}
                  bg={inputBg}
                  borderColor={inputBorderColor}
                  color={inputTextColor}
                  onChange={(e) => updateField("name", e.target.value)}
                />
              </FormControl>
            </HStack>
            <FormControl>
              <FormLabel fontSize="sm" color={labelColor}>
                Importance (1-5)
              </FormLabel>
              <NumberInput
                min={1}
                max={5}
                value={importanceValue}
                onChange={(_, valueAsNumber) =>
                  updateField("importance", clampImportance(valueAsNumber))
                }
              >
                <NumberInputField
                  bg={inputBg}
                  borderColor={inputBorderColor}
                  color={inputTextColor}
                />
              </NumberInput>
            </FormControl>
            {errorMessage ? (
              <Text fontSize="sm" color="red.500">
                {errorMessage}
              </Text>
            ) : null}
            {feedbackMessage && !isInline ? (
              <Text fontSize="sm" color="gwbi.500">
                {feedbackMessage}
              </Text>
            ) : null}
            <Button
              type="submit"
              colorScheme="gwbi"
              rightIcon={<FiSend />}
              isLoading={isSubmitting}
              alignSelf="flex-end"
            >
              Send
            </Button>
          </Box>
        </Box>
      ) : null}
    </Box>
  );
};

export default {
  FeedbackWidget,
  submitSuggestion,
};
